import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Me } from '../types';
import { useStore } from '../store';
import { mayOpen } from './homePage';
import { pageHref } from './newTab';
import { isPhoneApp } from '../components/App';   // /chat, /files and /email, where nothing is asked (report fce7e84d)

// ── Every app-wide pop-up has a home page, and is asked in ONE browser tab (report c2846304) ─────────
// "When AI brings up screen pop-ups, make them appear only on the relevant menu page. For example, Job
// Related should appear on the Active Job page. Filing Related should appear on the Filing page, etc. If
// the Filing page is not already open or the Active Job page is not already open, open the Active Job page
// tab automatically and pop up on that tab/page ONLY. Do not pop up on every tab/page as this is not
// productive the user does not know whether they have dealt with the pop-up already and gets confused."
//
// Until now each of these dialogs was mounted app-wide (App.tsx, AuthGate) and drew itself wherever it
// found itself: with six SLF tabs open, the same question was asked six times over, and answering it in
// one tab left the other five still asking until their next poll. Each pop-up now names the page it
// belongs on and asks this service whether THIS tab is the one to ask in:
//
//   usePopupHome(<page key>, <there is something to ask>, <the questions in hand, by name>) → 'show' | 'notice' | 'hidden'
//
//   'show'   — draw the dialog here: this tab is showing that page (or one of the two fallbacks below).
//   'notice' — this is the tab in front and no tab has the page open: draw ONE small card instead
//              (components/PopupHomeNotice) that opens the page on a click. A window.open with no click
//              behind it is blocked by every browser, so the card is how the page gets opened.
//   'hidden' — another tab has it, or a card for its page has been clicked — in this tab or in any other —
//              and the question went with the click. Draw nothing.
//
// The dialog is never drawn in two tabs at once, and nothing is dismissed or answered on anyone's behalf:
// a question skipped here is simply asked in the tab that owns its page.

export type PopupHome = 'show' | 'notice' | 'hidden';

// ── Home page per pop-up ─────────────────────────────────────────────────────────────────────────────
// The job questions (JobStatusConfirm, CloseJobWatcher, JobCommentPing, TrPassportPrompt) are all
// answered on Active Jobs; Our Documents' name questions and Drop Folder's proposals (NameChecks,
// DropProposals) on Filing.
export const HOME_JOBS = 'active_jobs';
export const HOME_FILING = 'doc_storage';

/** My Tasks' urgent alert: a job among its rows belongs on Active Jobs, and a money row on the page that
 *  row came from — the bank statement of its own account, or Petty Cash (routes/myTasks.ts `source`). */
export const tasksAlertHome = (alerts: Array<{ kind?: string; source?: string }>): string =>
  (alerts.some(a => a.kind === 'job') ? HOME_JOBS : String(alerts[0]?.source || ''));

/** The client-payment-slip pop-up: the page its own one button opens — the bank page of the account a
 *  lease-registration slip was paid into, the invoice a slip the client emailed against an invoice names
 *  (Bugify #550fb2e8), the quotation otherwise (components/SlipAlert). */
export const slipAlertHome = (slip: { source?: string; scope?: string } | undefined): string =>
  (!slip ? '' : slip.source === 'lease_reg' ? (slip.scope === 'client' ? 'bank_client' : 'bank_legal')
    : slip.source === 'email_invoice' ? 'invoice' : 'quotation');

/** Holds the home page of a pop-up that works it out from its own rows — My Tasks' urgent alert and the
 *  client-payment-slip pop-up above — still while the pop-up is up. Both read it off the FIRST of the
 *  rows the 5-minute poll returned, so a poll that came back in another order, or carrying a row the
 *  server named no page for (a blank key, which means "ask wherever the user is"), moved the same
 *  question between the dialog and the card on its own (report c2846304, opening #2). The page is settled
 *  once, when the question appears, and worked out afresh only once that question is gone. */
export function useHomeKey(key: string, pending: boolean): string {
  const held = useRef('');
  const settled = useRef(false);
  if (!pending) { settled.current = false; return key; }
  if (!settled.current) { settled.current = true; held.current = key; }
  return held.current;
}

// Every page any of them calls home. Only these take a window name (below): a tab showing any other page
// is left exactly as it is. 'outbox' is the Approvals tab, home of "New approvals have arrived"
// (components/ApprovalsArrived, report 327a232d); 'invoice' the home of a payment slip a client emailed
// against an invoice (components/SlipAlert, Bugify #550fb2e8).
// 'approvals_doc' and 'approvals_jobs' (report e58ddb81) are the same pop-up's home for Tracking and Filing, and for job-complete emails;
// 'email_check' (report 4aaa018b) for emails waiting on a supervisor's check.
const HOME_PAGES = new Set([HOME_JOBS, HOME_FILING, 'bank_acc', 'bank_legal', 'bank_client', 'pettycash', 'quotation', 'invoice', 'outbox', 'approvals_doc', 'approvals_jobs', 'email_check']);

/** The name the tab showing that page answers to — what openPopupHome's window.open targets. A caller
 *  that must not reload the tab it is already in (the Job Detail pill, report 4000e36d) compares it to
 *  window.name first. */
export const pageTabName = (key: string) => `slf-page-${key}`;

/** Whether this window already answers to a name of ours. A page claims the name of the page it shows only over a
 *  blank one or over another of these — never over a name the window was opened under for something else (PandaAI
 *  Email's EMAIL_TAB above all, report b6182c13). */
export const ourTabName = (name: string): boolean => name.indexOf('slf-page-') === 0;

// What a page is called on the card, in the words its own top-menu button uses: Filing reads "Filing"
// (tab_upload_doc), not the module name, as the browser tab does (App.tsx DocumentTitle).
const PAGE_LABEL: Record<string, string> = { doc_storage: 'tab_upload_doc', taken_return: 'tab_taken_return', approvals_doc: 'tab_approvals', approvals_jobs: 'tab_approvals', email_check: 'tab_approvals' };
export const pageLabelKey = (key: string) => PAGE_LABEL[key] || `mod_${key}`;

// ── Which page this tab is showing ───────────────────────────────────────────────────────────────────
// A module page says so in its own address, /m/<key>. The Dashboard and a My Dashboard(s) menu are one
// address whatever page is open on them, so they say which one themselves — and it is the page actually
// DRAWN, not the ?tab= the address asked for: the Dashboard falls back to Active Jobs, or to the first
// tab this login may open, whenever the requested one isn't theirs (Portal: effKey).
let dashPage = '';
const dashSubs = new Set<() => void>();

/** For the Dashboard (Portal: DashPageMark, off effKey) and Quick Launch (FocusDashboard, off the open
 *  page): while this is mounted, that is the page this tab shows. */
export function useDashPage(key: string): void {
  useEffect(() => {
    dashPage = key;
    dashSubs.forEach(f => f());
    return () => {
      if (dashPage !== key) return;   // another page has already taken over
      dashPage = '';
      dashSubs.forEach(f => f());
    };
  }, [key]);
}

const pageOfPath = (path: string): string =>
  (path.indexOf('/m/') === 0 ? (path.split('/')[2] || '') : path === '/' || path === '/focus' ? dashPage : '');

/** Read fresh, so the heartbeat and each look are right whether or not React has redrawn since. */
const pageHere = (): string => pageOfPath(window.location.pathname);

// ── The browser's tabs, as they tell each other where they are ───────────────────────────────────────
// Each tab writes {key, at, vis, ask} into one shared record every BEAT_MS: the page it is showing, when
// it last spoke, when it was last looked at, and which pages it is asking a question on (the claim
// below). A tab that has not spoken for GONE_MS is gone (closed, or asleep) and its page is up for grabs
// again; a question it was asking is held for it a while longer (CLAIM_MS — see the claim).
type Beat = { key: string; at: number; vis: number; ask?: Record<string, number> };
type Beats = Record<string, Beat>;
const TAB_KEY = 'mf_popup_tab';                                   // sessionStorage: this tab's own name
const beatsKey = (userId: string) => `mf_popupTabs:${userId}`;    // localStorage: all of them
const sentKey = (userId: string) => `mf_popupSent:${userId}`;     // localStorage: pages a tab was sent to
const doneKey = (userId: string) => `mf_popupDone:${userId}`;     // localStorage: pages a card handed its questions to
const BEAT_MS = 4_000;
const GONE_MS = 13_000;        // three beats and a little
const GRACE_MS = 6_000;        // a skipped beat is not a tab letting go of its question
const CLAIM_MS = 75_000;       // a question held by a tab the browser has sent to sleep (see below)
const SENT_MS = 20_000;        // a tab was sent to a page and the page never came up
const LOOK_MS = 2_000;         // how often a pop-up weighs the answer again

let tabId = '';
/** This tab's name, kept in sessionStorage so a refresh is the same tab and a new tab is a new one. */
function myTab(): string {
  if (tabId) return tabId;
  try {
    const kept = sessionStorage.getItem(TAB_KEY);
    if (kept) { tabId = kept; return tabId; }
  } catch { /* no session storage — a name for this page's life does just as well */ }
  tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  try { sessionStorage.setItem(TAB_KEY, tabId); } catch { /* as above */ }
  return tabId;
}

/** null = this browser will not let us share anything (site data blocked, private mode, quota) — the
 *  first of the two fallbacks: such a tab asks its questions itself, exactly as every tab used to. */
function readBeats(userId: string): Beats | null {
  let raw: string | null = null;
  try { raw = localStorage.getItem(beatsKey(userId)); } catch { return null; }
  let v: any = null;
  try { v = JSON.parse(raw || '{}'); } catch { v = null; }   // garbage: the next beat writes it afresh
  const out: Beats = {};
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    for (const [id, b] of Object.entries<any>(v)) {
      if (!b || !Number.isFinite(b.at) || !Number.isFinite(b.vis)) continue;
      const one: Beat = { key: String(b.key || ''), at: b.at, vis: b.vis };
      // The pages that tab says it is asking on, kept as it wrote them — every tab copies the others'
      // entries through untouched, so a claim is never lost by somebody else's heartbeat.
      if (b.ask && typeof b.ask === 'object' && !Array.isArray(b.ask)) {
        const ask: Record<string, number> = {};
        for (const [k, t] of Object.entries<any>(b.ask)) if (k && Number.isFinite(t)) ask[k] = t;
        if (Object.keys(ask).length) one.ask = ask;
      }
      out[id] = one;
    }
  }
  return out;
}

/** A shared page key → when record (the sent marks, the handed-over marks): empty when unshared or garbage. */
function readMarks(name: string): Record<string, number> {
  let raw: string | null = null;
  try { raw = localStorage.getItem(name); } catch { return {}; }
  try { const v = JSON.parse(raw || '{}'); return v && typeof v === 'object' && !Array.isArray(v) ? v : {}; } catch { return {}; }
}
function readSent(userId: string): Record<string, number> { return readMarks(sentKey(userId)); }
function writeSent(userId: string, v: Record<string, number>): void {
  try { localStorage.setItem(sentKey(userId), JSON.stringify(v)); } catch { /* unshared — see readBeats */ }
}

/** When that tab last said it was asking something, on any page — 0 when it is asking nothing. */
function latestClaim(b: Beat): number {
  let t = 0;
  const ask = b.ask || {};
  for (const k of Object.keys(ask)) t = Math.max(t, Number(ask[k]) || 0);
  return t;
}

let lastSeen = Date.now();     // when this tab was last looked at
let beatTimer = 0;
let beaters = 0;
let beatFor = '';              // the login the heartbeat is running for
let beatPage: string | null = null;   // the page the last beat reported

function beat(userId: string): void {
  const now = Date.now();
  if (document.visibilityState === 'visible') lastSeen = now;
  const beats = readBeats(userId);
  if (!beats) return;
  const page = pageHere();
  beatPage = page;
  const self: Beat = { key: page, at: now, vis: lastSeen };
  // The pages this tab is asking on, re-stamped while it still is: a claim lives on the heartbeat behind
  // it, so a tab that is killed outright lets its questions go once that heartbeat has stopped.
  const ask: Record<string, number> = {};
  for (const k of Object.keys(myClaims)) { myClaims[k] = now; ask[k] = now; }
  if (Object.keys(ask).length) self.ask = ask;
  // A phone app asks nothing (usePopupHome, below), so it is no tab to ask in either (report fce7e84d): it keeps
  // itself off the record rather than stand in it as the tab in front, where a question would wait unseen behind a
  // full-screen chat while every office tab kept quiet for it.
  const next: Beats = isPhoneApp(window.location.pathname) ? {} : { [myTab()]: self };
  for (const [id, b] of Object.entries(beats)) {
    if (id === myTab()) continue;
    // A tab is dropped once its heartbeat has stopped (GONE_MS) — unless it is still asking a question
    // somewhere, which keeps its place a while longer (CLAIM_MS). A browser throttles a background tab's
    // timers to about one a minute, and dropping its place between two of those beats is what let the
    // other tabs take its question and give it straight back. A tab that is CLOSED is not waited for:
    // it says so itself on the way out (leave, below).
    if (now - b.at < GONE_MS || now - latestClaim(b) < CLAIM_MS) next[id] = b;
  }
  try { localStorage.setItem(beatsKey(userId), JSON.stringify(next)); } catch { /* unshared — see readBeats */ }
  // This tab has the page up, so whatever was sent to it has arrived.
  if (page) {
    const sent = readSent(userId);
    if (page in sent) { delete sent[page]; writeSent(userId, sent); }
  }
}

/** A page change is said at once rather than at the next beat — once, however many pop-ups notice it. */
function beatMoved(userId: string): void {
  if (pageHere() === beatPage) return;
  beat(userId);
}

/** Leaves the shared record, so a tab being closed doesn't hold its page — or a question it was asking —
 *  for another GONE_MS. A tab says this on the way out, which is why nothing has to wait CLAIM_MS for a
 *  tab that was simply closed. */
function leave(userId: string): void {
  const beats = readBeats(userId);
  if (!beats || !(myTab() in beats)) return;
  delete beats[myTab()];
  try { localStorage.setItem(beatsKey(userId), JSON.stringify(beats)); } catch { /* unshared */ }
}

/** The tab in front of the others: one that was being looked at when it last spoke (`vis >= at`), and
 *  among those simply the first name in order. NOT the one looked at most recently: with two windows
 *  side by side both are visible and both re-stamp `vis` at every beat, so "most recently" handed the
 *  turn from one to the other every 4 seconds and the same question appeared, vanished and appeared
 *  again (report c2846304, opening #2). `live` comes in sorted. A phone app is never among them: it keeps
 *  itself off the record (beat). */
function frontTab(beats: Beats, live: string[]): string {
  for (const id of live) if (beats[id].vis >= beats[id].at) return id;
  return live[0] || '';
}

// ── Which tab is asking: the claim ───────────────────────────────────────────────────────────────────
// The answer used to be worked out afresh in every tab every LOOK_MS from where the windows were at that
// moment, so a question moved the instant the front tab changed — and it changed every beat. Opening #2
// of report c2846304: "seems to get stuck in a loop… the requests that are not related to that screen
// pop up, then disappear. Then the notification pops up, disappears, and the requests pop up again."
//
// The decision is now LATCHED. A tab that puts a question up writes a claim into its own beat (`ask`:
// page key → when it last said so, re-stamped by every heartbeat) and keeps that question while it still
// has something to ask and is still beating; every other tab stays quiet. It lets go when the question is
// answered or gone, when the tab is closed (`leave` on pagehide), or when the tab stops beating
// altogether for CLAIM_MS. Only two things move a question off a claiming tab: the page it belongs on
// coming up ON SCREEN in another tab (that is where it belongs, however it was opened), and the claiming
// tab being neither on that page nor in front of the user any more — otherwise a question could sit
// unseen in a window the user had left behind. A window coming to the front, a visibility change or a
// skipped heartbeat move nothing at all.
const claimers: Record<string, Set<number>> = {};   // page key → the pop-ups in THIS tab that want it
const myClaims: Record<string, number> = {};        // page key → when this tab claimed it

// One card per page however many pop-ups call that page home: the first to ask holds it until it has
// nothing left to ask or is unmounted. It used to be let go at any look whose answer was not 'notice'
// and taken over by the next pop-up at its own look, which is the other half of the blinking above.
const cardHeld: Record<string, number> = {};

/** This pop-up lets go of a page. @returns whether the shared record now has to be written again. */
function drop(key: string, askerId: number): boolean {
  if (cardHeld[key] === askerId) delete cardHeld[key];
  const set = claimers[key];
  if (!set || !set.delete(askerId) || set.size) return false;   // another pop-up here still asks on it
  delete claimers[key];
  if (!(key in myClaims)) return false;
  delete myClaims[key];
  return true;
}

/** This tab is the one asking on `key`: claim it, and let go of any page this pop-up claimed before. */
function hold(userId: string, key: string, askerId: number): void {
  let write = false;
  for (const k of Object.keys(claimers)) if (k !== key) write = drop(k, askerId) || write;
  const set = claimers[key] || (claimers[key] = new Set<number>());
  set.add(askerId);
  if (!(key in myClaims)) { myClaims[key] = Date.now(); write = true; }
  if (write && userId) beat(userId);
}

/** This pop-up has nothing left to ask, or has gone: every page it claimed is let go at once rather than
 *  at the next beat, so the tab that should ask next hears about it now. */
function letGo(userId: string, askerId: number): void {
  let write = false;
  for (const k of Object.keys(claimers)) write = drop(k, askerId) || write;
  if (write && userId) beat(userId);
}

/** Who asks this page's question — worked out the same way in every tab, from the shared record alone, so
 *  all of them arrive at the one answer. `live` comes in sorted, and so does every list here, so two tabs
 *  that put the question up in the same breath still settle on one of them. */
function askerFor(beats: Beats, live: string[], key: string, now: number): string {
  const all = Object.keys(beats).sort();
  const watched = (id: string) => beats[id].vis >= beats[id].at;   // looked at when it last spoke
  const onPage = (id: string) => beats[id].key === key;
  const claims = all.filter(id => { const t = Number(beats[id].ask?.[key]) || 0; return t > 0 && now - t < CLAIM_MS; });
  // 1. The tab that has the page open AND the question already in it: a half-written answer never moves
  //    tabs, and a tab the browser has throttled keeps its turn instead of losing it between heartbeats.
  const asking = claims.filter(onPage);
  if (asking.length) return asking[0];
  // 2. The page is open in a tab the user is looking at — that is where the question belongs, so it moves
  //    there the moment the page comes up, however it was opened.
  const here = live.filter(id => onPage(id) && watched(id));
  if (here.length) return here[0];
  // 3. A tab already asking somewhere else, while it is still the one in front of the user.
  const still = claims.filter(watched);
  if (still.length) return still[0];
  // 4. The page is open in a tab the user is not looking at; 5. nobody has it: the tab in front.
  const open = live.filter(onPage);
  return open[0] || frontTab(beats, live);
}

// The tab this one last worked out as the asker, per page, and when. A tab that has merely skipped a
// heartbeat has not let go — a browser throttles a background tab's timers to about one a minute — so its
// turn stands for one beat longer before the question is moved anywhere. Without that grace the question
// was handed to the tab in front and handed straight back at the next beat.
const heldBy: Record<string, { id: string; at: number }> = {};

// Pages this tab sent the user to and that never came up. Kept here rather than in the shared record so
// it lasts exactly as long as this page does: a reload asks with a card again. Once set it STAYS set: it
// used to be forgotten the moment any tab reported the page, which a background tab does once a minute,
// and the question then fell back to the card and came round to the dialog again.
//
// A page in here is one this tab has finished asking about (report 010c0f53): the click opened it, so the
// question belongs in the tab that now shows it, and this tab — which is on some other page entirely —
// goes quiet for good rather than drawing the dialog on a page the question has nothing to do with. It
// used to escalate to the full dialog here after 20 seconds, which is how the notice came back on the
// irrelevant page even after it had been clicked.
const gaveUp = new Set<string>();

// ── A clicked card hands the question over in EVERY tab (report 3f8007d0) ──────────────────────────────
// Opening #2 of the report: "even if you click on this notification and launch the filing tab, it does not
// automatically remove this notification from any other tab (which is frustrating because you have to refresh
// the page) … as long as all notifications that are the same disappear immediately upon clicking."
//
// gaveUp above is this tab's alone, and every other tab kept the questions it had polled for itself: it kept
// quiet only while the asker worked out above was another tab, so the moment the Filing tab left Filing, was
// closed or was put to sleep by the browser, the same card came back in whichever tab was in front — often for
// a question already answered over there — until each tab was refreshed. The click is now written into the
// shared record as well (page key → when). From then on a tab that is not on that page leaves every question
// it already had for that page to the tab the click opened, and offers the card again only for a question
// that turns up after the click. Every tab hears it at once and asks the server again (POPUP_REFRESH, below).
function readDone(userId: string): Record<string, number> { return readMarks(doneKey(userId)); }
function handOver(userId: string, key: string): void {
  const done = readDone(userId);
  done[key] = Date.now();
  try { localStorage.setItem(doneKey(userId), JSON.stringify(done)); } catch { /* unshared — see readBeats */ }
}

/** `arrived`: when the question in hand turned up in this tab (usePopupHome). */
function weigh(userId: string, me: Me, key: string, page: string, arrived: number): PopupHome {
  const beats = readBeats(userId);
  if (!beats) return 'show';                                    // fallback 1: nothing is shared here
  // Handed over: a card for this page was clicked after this question turned up here, so it went with the
  // click, whichever tab would otherwise ask it. The page's own tab still asks; so does a login that cannot
  // open the page (fallback 2), which never had a card to click.
  if (page !== key && mayOpen(key, me)) {
    const over = Number(readDone(userId)[key]) || 0;
    if (over && arrived <= over) return 'hidden';
  }
  const now = Date.now();
  const mine = myTab();
  const live = Object.keys(beats).filter(id => now - beats[id].at < GONE_MS).sort();
  let asker = askerFor(beats, live, key, now) || mine;
  const was = heldBy[key];
  if (was && was.id !== asker && live.indexOf(was.id) < 0 && now - was.at < GRACE_MS) asker = was.id;
  else heldBy[key] = { id: asker, at: now };
  if (asker !== mine) return 'hidden';                          // another tab is asking it
  // This tab is the one. The dialog where the page itself is open, or where this login cannot open that
  // page at all (fallback 2, services/homePage.ts: My Tasks and the payment-slip pop-up both reach people
  // who cannot open the bank pages); the card where the page is open in no tab.
  if (page === key) return 'show';
  if (!mayOpen(key, me)) return 'show';
  if (gaveUp.has(key)) return 'hidden';                         // asked and opened from here: not ours any more
  const sent = Number(readSent(userId)[key]) || 0;
  if (!sent) return 'notice';                                   // offer the card
  if (now - sent < SENT_MS) return 'hidden';                    // the page is on its way
  // The card was clicked and the page still has not reported in. The question stays with the tab the
  // click opened — this tab asks no more about that page while this page load lasts.
  gaveUp.add(key);
  const left = readSent(userId);
  if (key in left) { delete left[key]; writeSent(userId, left); }
  return 'hidden';
}

/** The card's click: the page opens in the tab that already answers to it, or in a new one, and this
 *  round is marked as sent — and as handed over, after which every tab but the one now showing the page
 *  leaves it that page's questions (report 3f8007d0). Called from a real click, so the browser allows the
 *  window.
 *
 *  Unless it doesn't: a browser that refuses the window hands back null, and then nothing was launched at
 *  all, so nothing is handed over, the sent mark is taken straight back off and the card stays up to be
 *  clicked again. @returns whether the page was launched. */
export function openPopupHome(userId: string, key: string): boolean {
  if (userId) {
    const sent = readSent(userId);
    sent[key] = Date.now();
    writeSent(userId, sent);
  }
  const win = window.open(pageHref(key), pageTabName(key));
  if (win) {
    if (userId) handOver(userId, key);
    return true;
  }
  if (!userId) return false;
  const back = readSent(userId);
  if (key in back) { delete back[key]; writeSent(userId, back); }
  return false;
}

// ── Asking the server again (report 3f8007d0) ────────────────────────────────────────────────────────
// A pop-up's questions are its own poll's, one tab at a time, and NameChecks never looked again while it held
// any — so a question answered in the Filing tab stayed in every other tab until that tab was refreshed. This
// is said in a tab when a card has just been clicked in another, and whenever the tab comes up again; the
// pop-ups that poll for their questions (NameChecks, DropProposals and the job pop-ups) ask again on it.
export const POPUP_REFRESH = 'slf:popup-refresh';
const REFRESH_MS = 250;        // said once, however many pop-ups heard it and however many ways (tab, focus)
let refreshTimer = 0;
function refreshPopups(): void {
  if (refreshTimer) return;
  refreshTimer = window.setTimeout(() => { refreshTimer = 0; window.dispatchEvent(new Event(POPUP_REFRESH)); }, REFRESH_MS);
}

// ── Chat: the window over a page and the full-screen page are one chat (report 14dc81ac) ─────────────
// Calem: "Make chat its own full-screen tab but allow the user to reduce the size to the current window size that
// you see on the screen." The full-screen chat has existed all along — /chat, the address a phone's installed Chat
// opens (components/Chat/ChatApp) — but nothing on the floating window led up to it and nothing up there led back
// down, so the two shapes of the one chat could not be swapped. The ⤢ in the window's top row and the ⤡ in the
// page's are those two doors, walked with the machinery above: the page is opened BY NAME, so a press of ⤢ while a
// chat tab is already open brings that tab forward instead of stacking a second chat beside it (report c2846304).
//
// Going up: the tab the press came from writes down which tab it is and which page it was on, and its window closes
// behind it. Coming down: the chat page hands the window back to that tab and closes itself. The tab it is handed to
// takes the note off the shared record as it puts the window back up, so a moment later the chat page can tell
// whether it was heard at all: where that tab has been closed in the meantime nobody answers, and the chat page
// walks back to the page the window came from itself rather than taking the chat off the screen altogether.
// The chat is the same component in both frames (ChatPanel), so nothing is re-asked or lost in between.
const CHAT_FROM = 'mf_chatFrom';      // localStorage: the tab a chat page was just sent out from
const CHAT_MINE = 'mf_chatFromTab';   // sessionStorage: that chat tab's own copy of it, kept across a reload
const CHAT_BACK = 'mf_chatBack';      // localStorage: the tab the window is being handed back to
const FROM_MS = 30_000;               // a handoff still being walked; anything older belongs to nobody
const BACK_MS = 120_000;              // long enough for the page the window goes back onto to load
const HEARD_MS = 400;                 // how long the tab it is handed to has to take it

/** Which tab a full-screen chat came up from, and the page that tab was on. */
export type ChatFrom = { tab: string; path: string };

/** The name the chat's own tab answers to. /chat is a page of its own and no module, so it is in neither HOME_PAGES
 *  nor pageHref — its name is minted the same way as theirs, and the page claims it while it is showing (ChatApp). */
export const CHAT_TAB_NAME = pageTabName('chat');

const readFrom = (raw: string | null, within = 0): ChatFrom | null => {
  let v: any = null;
  try { v = JSON.parse(raw || 'null'); } catch { return null; }
  if (!v || typeof v.tab !== 'string' || !v.tab || typeof v.path !== 'string' || !v.path) return null;
  if (within && !(Number.isFinite(v.at) && Date.now() - v.at < within)) return null;
  return { tab: v.tab, path: v.path };
};

/** ⤢ in the chat window: the chat page opens in a tab of its own — the chat tab already open, if there is one — and
 *  this tab is written down as the one holding that window, so the ⤡ up there knows where to hand it back.
 *  @returns false where the browser refused the tab, so the window can stay open rather than the chat going
 *  nowhere at all. */
export function openChatTab(href: string): boolean {
  const from = { tab: myTab(), path: window.location.pathname + window.location.search, at: Date.now() };
  try { localStorage.setItem(CHAT_FROM, JSON.stringify(from)); } catch { /* unshared (see readBeats): the page still opens, with no way back down */ }
  const win = window.open(href, CHAT_TAB_NAME);
  if (!win) {
    try { localStorage.removeItem(CHAT_FROM); } catch { /* as above */ }
    return false;
  }
  try { win.focus(); } catch { /* the browser decides what comes forward */ }
  return true;
}

/** The chat page's own way back down, for as long as this tab shows the chat: the tab its window came from. Taken
 *  off the shared record and kept in this tab's own, so a reload still knows the way back and no second chat tab
 *  ever reads the same handoff as its own. Nothing to hand back to — a chat tab opened by hand, and the app a phone
 *  installed — answers null, and no ⤡ is drawn there. */
export function chatCameFrom(): ChatFrom | null {
  // A handoff just left on the shared record is this page's, and the newest one wins: ⤢ pressed in a second tab
  // while the chat tab is open sends the chat out again, and the way back down is then to THAT tab rather than to
  // whichever one expanded it first.
  let fresh: ChatFrom | null = null;
  try { fresh = readFrom(localStorage.getItem(CHAT_FROM), FROM_MS); } catch { /* unshared: only this tab's own copy, below */ }
  if (fresh) {
    try { localStorage.removeItem(CHAT_FROM); } catch { /* unshared */ }
    try { sessionStorage.setItem(CHAT_MINE, JSON.stringify(fresh)); } catch { /* then the way back lasts this page load alone */ }
    return fresh;
  }
  // Nothing new on it: the copy this tab kept, so a reload of the chat page still knows its way back down.
  let mine: ChatFrom | null = null;
  try { mine = readFrom(sessionStorage.getItem(CHAT_MINE)); } catch { /* no session storage: no way back down here */ }
  return mine;
}

/** @returns whether the note could be left at all — a browser that shares nothing between its tabs (see readBeats)
 *  has no way to hand the window back, and the chat page walks down to the page itself instead. */
const writeBack = (tab: string): boolean => {
  try { localStorage.setItem(CHAT_BACK, JSON.stringify({ tab, at: Date.now() })); return true; } catch { return false; }
};
const readBack = (): string => {
  let v: any = null;
  try { v = JSON.parse(localStorage.getItem(CHAT_BACK) || 'null'); } catch { return ''; }
  if (!v || typeof v.tab !== 'string' || !Number.isFinite(v.at) || Date.now() - v.at > BACK_MS) return '';
  return v.tab;
};

/** ⤡ on the chat page: the window goes back up on the tab it came from and this tab closes behind it. */
export function chatReduce(from: ChatFrom): void {
  if (window.name === CHAT_TAB_NAME) window.name = '';   // this tab is the chat's no longer
  try { sessionStorage.removeItem(CHAT_MINE); } catch { /* no session storage: nothing was kept there anyway */ }
  if (!writeBack(from.tab)) { window.location.assign(from.path); return; }   // nothing can be handed anywhere here
  window.setTimeout(() => {
    if (!readBack()) {
      // That tab has the window back up. This one closes — and a browser only lets a script close a tab a script
      // opened, so one that refuses is walked out to the page the window went back onto instead of being left
      // sitting on a chat that is no longer here.
      window.close();
      window.setTimeout(() => { if (!window.closed) window.location.assign(from.path); }, HEARD_MS);
      return;
    }
    // Nobody answered: the tab the window came from has been closed while the chat was full-screen. This tab
    // becomes the page it was on, and the window comes back up here.
    writeBack(myTab());
    window.location.assign(from.path);
  }, HEARD_MS);
}

/** Where a reduced chat window comes back up: the Dashboard's own Chat button (ChatPanel: ChatTopButton) and a My
 *  Dashboard's (FocusDashboard), the two menus the window is ever opened from. Only a note addressed to THIS tab is
 *  answered, and answering takes it off the record — which is also how the chat page learns it was heard. */
export function useChatBack(open: () => void): void {
  const ref = useRef(open);
  ref.current = open;
  useEffect(() => {
    const take = () => {
      if (readBack() !== myTab()) return;
      try { localStorage.removeItem(CHAT_BACK); } catch { /* unshared */ }
      ref.current();
    };
    take();     // the window may be coming back onto a page this tab is only now loading
    const shared = (e: StorageEvent) => { if (!e.key || e.key === CHAT_BACK) take(); };
    window.addEventListener('storage', shared);
    return () => { window.removeEventListener('storage', shared); };
  }, []);
}

let askerSeq = 0;    // names the pop-ups in this tab apart (cardHeld / claimers above)

/** Where a card sits in the corner's stack: with a question waiting on two different pages, the second
 *  card lines up under the first instead of on top of it. */
export const popupHomeSlot = (key: string): number => {
  const i = Object.keys(cardHeld).sort().indexOf(key);
  return i < 0 ? 0 : i;
};

/** `asks`: the questions this pop-up has in hand, by name, whether or not it may put them up right now. */
export function usePopupHome(key: string, pending: boolean, asks?: readonly string[]): PopupHome {
  const userId = useStore(s => s.me?.userId) || '';
  const loc = useLocation();
  const [, bump] = useState(0);
  const idRef = useRef(0);
  if (!idRef.current) idRef.current = ++askerSeq;
  const askerId = idRef.current;

  // When the question in hand turned up here: what a clicked card is weighed against (weigh, report 3f8007d0).
  // A pop-up that names its questions is timed by the newest of them — a question joining a list that was
  // handed over is still new, while the server handing the same list back, or the pop-up holding back while
  // somebody types (the job pop-ups do), brings nothing new. One that names none is timed from when it last
  // had something to ask.
  const turnedUp = useRef<Record<string, number>>({});
  const since = useRef(0);
  const arrived = useRef(0);
  const at = Date.now();
  if (asks) {
    const kept: Record<string, number> = {};
    let newest = 0;
    for (const a of asks) { kept[a] = turnedUp.current[a] || at; newest = Math.max(newest, kept[a]); }
    turnedUp.current = kept;
    arrived.current = newest;
  } else {
    since.current = pending ? since.current || at : 0;
    arrived.current = since.current;
  }
  // A Dashboard tab change is not an address change, so it is heard rather than re-read. The one look on
  // subscribing catches a Dashboard that said which page it has between this render and this effect — it
  // draws before these pop-ups do, so on a sign-in its word would otherwise be missed.
  useEffect(() => {
    const f = () => bump(n => n + 1);
    dashSubs.add(f);
    f();
    return () => { dashSubs.delete(f); };
  }, []);
  const page = pageOfPath(loc.pathname);
  // The phone apps ask nothing at all (report fce7e84d) — not even a pop-up with no page of its own, which is
  // otherwise asked wherever the user is — and hold no question, which is left to an office tab instead.
  const phone = isPhoneApp(loc.pathname);

  // The window name that lets another tab's card open the page HERE rather than in a tab of its own:
  // window.open(pageHref(key), 'slf-page-<key>') finds it by name. Never over a name this window already
  // answers to — PandaAI Email's EMAIL_TAB above all (NewMailToast, report b6182c13), which is how a
  // new-mail card finds the Email tab.
  useEffect(() => {
    // Say where this tab is NOW rather than at the next beat, so two tabs opening the same page a second
    // apart never both read themselves as the one showing it.
    if (userId) beatMoved(userId);
    if (!page || !HOME_PAGES.has(page)) return;
    const name = pageTabName(page);
    if (window.name && !ourTabName(window.name)) return;
    window.name = name;
    return () => { if (window.name === name) window.name = ''; };
  }, [page, userId]);

  // The heartbeat is the tab's, not this pop-up's: one timer however many pop-ups are mounted. A tab
  // somebody else signs into starts a new one, under the new login's own record.
  useEffect(() => {
    if (!userId) return;
    beaters++;
    if (beatTimer && beatFor !== userId) { window.clearInterval(beatTimer); beatTimer = 0; }
    if (!beatTimer) {
      beatFor = userId;
      beat(userId);
      beatTimer = window.setInterval(() => beat(userId), BEAT_MS);
    }
    // Said both ways round: a tab the user has just left says so at once (its beat stamps `at` past
    // `vis`), instead of reading as the tab in front for up to another 4 seconds.
    const seen = () => beat(userId);
    const gone = () => leave(userId);
    document.addEventListener('visibilitychange', seen);
    window.addEventListener('pagehide', gone);
    return () => {
      document.removeEventListener('visibilitychange', seen);
      window.removeEventListener('pagehide', gone);
      beaters--;
      if (beaters <= 0 && beatTimer) { window.clearInterval(beatTimer); beatTimer = 0; beatFor = ''; }
    };
  }, [userId]);

  const [state, setState] = useState<PopupHome>('hidden');
  useEffect(() => {
    const look = () => {
      const who = useStore.getState().me;
      let next: PopupHome = 'hidden';
      // A pop-up with no page of its own to go to — My Tasks' alert can carry a row the server named
      // nothing for — is asked where the user is, as it always was.
      if (pending && !phone) next = !key ? 'show' : who?.userId ? weigh(who.userId, who, key, pageHere(), arrived.current) : 'hidden';
      // While this tab is the one asking on the page it says so in the shared record, so no other tab
      // takes the question over at its next look; it lets go the moment it has nothing to ask there.
      if (pending && !!key && (next === 'show' || next === 'notice')) hold(who?.userId || '', key, askerId);
      else letGo(who?.userId || '', askerId);
      if (next === 'notice') {
        const held = cardHeld[key];
        if (held && held !== askerId) next = 'hidden';            // another pop-up here already has the card
        else cardHeld[key] = askerId;
      }
      setState(next);
    };
    look();
    const timer = window.setInterval(look, LOOK_MS);
    // Another tab taking the page, being sent to it, or clicking a card, is heard at once rather than at the
    // next look. A clicked card also has every pop-up here ask the server again (report 3f8007d0).
    const shared = (e: StorageEvent) => {
      if (e.key && e.key.indexOf('mf_popup') !== 0) return;
      look();
      if (!e.key || e.key === doneKey(userId)) refreshPopups();
    };
    // …and so is this tab coming up again — clicked to from another tab, or its window clicked into — when
    // everything it holds may have been answered somewhere else in the meantime.
    const up = () => {
      if (document.visibilityState !== 'visible') return;
      look();
      refreshPopups();
    };
    window.addEventListener('storage', shared);
    document.addEventListener('visibilitychange', up);
    window.addEventListener('focus', up);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('storage', shared);
      document.removeEventListener('visibilitychange', up);
      window.removeEventListener('focus', up);
    };
  }, [key, pending, page, phone, userId, askerId]);

  // The claim and the card go when this pop-up goes — not at every change of what it is asking about,
  // which is what let them change hands between pop-ups while the question itself stayed up.
  useEffect(() => () => letGo(useStore.getState().me?.userId || '', askerId), [askerId]);

  return phone ? 'hidden' : state;
}
